/**
 * SceneComposition — Remotion composition for a single scene.
 *
 * This is the bridge between the show system and Remotion's renderer.
 * It receives SceneProps, dispatches to the correct layout component.
 * Registered in Root.tsx as composition id: 'scene'.
 */
import React from 'react';
import { Audio } from 'remotion';
import type { SceneProps, SceneLayoutId } from '../types';
import { SceneContext } from './SceneLayouts/shared';
import {
  L1_FullScreenVisual,
  L2_VisualLeftTextRight,
  L3_TextLeftVisualRight,
  L4_TwoVisualSplit,
  L5_FullScreenHeadline,
  L6_VisualPlusBottomHeadline,
  L7_BigNumberGraphic,
  L8_QuoteLayout,
  L10_MultiImageGrid,
  L12_TextHighlight,
  L13_LowerThird,
} from './SceneLayouts';
import { GlobalOverlay } from './GlobalOverlay';
import { CustomLayerRenderer } from './CustomLayerRenderer';
import type { ComponentType } from 'react';

// ─── Layout registry ──────────────────────────────────────────
// Add new layouts here — no changes needed elsewhere.

const LAYOUT_REGISTRY: Record<SceneLayoutId, ComponentType<SceneProps>> = {
  L1:  L1_FullScreenVisual,
  L2:  L2_VisualLeftTextRight,
  L3:  L3_TextLeftVisualRight,
  L4:  L4_TwoVisualSplit,
  L5:  L5_FullScreenHeadline,
  L6:  L6_VisualPlusBottomHeadline,
  L7:  L7_BigNumberGraphic,
  L8:  L8_QuoteLayout,
  L10: L10_MultiImageGrid,
  L11: L1_FullScreenVisual,  // L11 (Video Focus) falls back to L1 — video gets picked as primary
  L12: L12_TextHighlight,
  L13: L13_LowerThird,
};

// ─── SceneComposition ─────────────────────────────────────────

export const SceneComposition: React.FC<SceneProps> = (props) => {
  const LayoutComponent = LAYOUT_REGISTRY[props.layoutId] ?? L1_FullScreenVisual;
  return (
    <SceneContext.Provider value={{
      videoMuted: props.videoMuted ?? false,
      hideImage: props.hideImage ?? false,
      hideVideo: props.hideVideo ?? false,
      contentLayerTransforms: props.contentLayerTransforms,
    }}>
      {/* Scene layout renders first (background + content) */}
      <LayoutComponent {...props} />
      {/* Custom user-added layers (sorted by zIndex ascending) */}
      {props.customLayers
        ?.slice()
        .sort((a, b) => a.zIndex - b.zIndex)
        .map((layer) => (
          <CustomLayerRenderer key={layer.id} layer={layer} />
        ))}
      {/* Global overlays float on top of everything */}
      <GlobalOverlay props={props} />
      {/* Voiceover — seeked to audioOffsetSec so it stays continuous across scene cuts */}
      {props.voiceoverUrl && (
        <Audio
          src={props.voiceoverUrl}
          volume={props.voiceoverVolume ?? 1}
          startFrom={Math.round((props.audioOffsetSec ?? 0) * (props._fps ?? 30))}
        />
      )}
      {/* bgMusic: NOT rendered in preview mode (persistent HTML audio element handles it there).
          In render mode: seeked to scene's global start time so it never restarts. */}
      {props.bgMusicUrl && props._renderMode !== 'preview' && (
        <Audio
          src={props.bgMusicUrl}
          volume={props.bgMusicVolume ?? 0.3}
          startFrom={Math.round((props.audioOffsetSec ?? 0) * (props._fps ?? 30))}
        />
      )}
    </SceneContext.Provider>
  );
};

// ─── Default props for Remotion Studio / bundler ──────────────

export const defaultSceneProps: SceneProps = {
  layoutId: 'L1',
  headline: 'Breaking News Headline Here',
  subheadline: 'Subheadline with supporting context',
  assets: {},
  colorPalette: {
    primary: '#0A1628',
    secondary: '#1A3A6B',
    accent: '#E8C84A',
    background: '#0D1F3C',
    text: '#FFFFFF',
    ticker: '#0A1628',
    tickerText: '#FFFFFF',
    breakingBanner: '#CC0000',
    breakingText: '#FFFFFF',
  },
  headlineFont: {
    fontFamily: 'Roboto',
    fontSize: 52,
    fontWeight: 700,
    color: '#FFFFFF',
    letterSpacing: -0.5,
    lineHeight: 1.2,
  },
  subheadlineFont: {
    fontFamily: 'Roboto',
    fontSize: 28,
    fontWeight: 400,
    color: '#CCCCCC',
    letterSpacing: 0,
    lineHeight: 1.4,
  },
  showTicker: true,
  tickerText: 'Latest news · Breaking updates · Stay tuned',
  showStoryCounter: false,
  isBreaking: false,
};
